/**
 * Documents Section
 * File upload and preview (create mode only)
 */

import { FileUpload } from '@/components/application/file-upload/file-upload-base';
import { FileUploadPreviewInline } from '@/components/application/documents/file-upload-preview-inline';
import type { UploadFileItem } from '@/components/application/documents/file-upload-preview-modal';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/toast-service';

interface DocumentsSectionProps {
    selectedFiles: UploadFileItem[];
    onFilesChange: (files: UploadFileItem[]) => void;
    onFileDrop: (files: FileList) => void;
    onRemoveFile: (fileId: string) => void;
    onAddFiles: () => void;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    isUploading: boolean;
}

export function DocumentsSection({
    selectedFiles,
    onFilesChange,
    onFileDrop,
    onRemoveFile,
    onAddFiles,
    fileInputRef,
    isUploading,
}: DocumentsSectionProps) {
    const { t } = useTranslation('pages:containers');

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.documents', 'Documents')}</h2>
            <p className="text-sm text-tertiary mb-4">
                {t('form.documentsDescription', 'Upload any documents related to this container (optional). You can also upload documents later.')}
            </p>
            <FileUpload.Root>
                <FileUpload.DropZone
                    hint={t('form.documentsHint', 'PDF, PNG, JPG, XLSX up to 10MB')}
                    accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls"
                    allowsMultiple
                    maxSize={10 * 1024 * 1024}
                    onDropFiles={onFileDrop}
                    onSizeLimitExceed={() => {
                        toast.error(t('form.fileTooLarge', 'File is too large. Maximum 10MB allowed.'));
                    }}
                />
            </FileUpload.Root>

            {/* Hidden file input for "Add More" button */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls"
                multiple
                className="hidden"
                onChange={(e) => {
                    if (e.target.files) {
                        onFileDrop(e.target.files);
                    }
                    e.target.value = '';
                }}
            />

            {/* File Preview with Document Type Selection */}
            {selectedFiles.length > 0 && (
                <div className="mt-4">
                    <FileUploadPreviewInline
                        files={selectedFiles}
                        onFilesChange={onFilesChange}
                        onAddFiles={onAddFiles}
                        onUpload={() => { }}
                        onRemoveFile={onRemoveFile}
                        isUploading={isUploading}
                        hideUploadButton
                    />
                </div>
            )}
        </div>
    );
}
